import { ResourceCalendar } from '@/components/calendar/ResourceCalendar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { WhatIfCommitResult } from '@/components/calendar/BurnoutRiskPanel';
import { WhatIfScenario, type PlannerNavContext } from '@/types';
import { type PlanPreviewScenario } from '@/types/resourceRequest';

interface DashboardProps {
  heatmapMode?: boolean;
  /** Pre-selects a project in the heatmap filter when navigating from Project Staffing */
  initialProjectFilter?: string | null;
  /** Scenario handed off from the heatmap suggestions panel — calendar opens in what-if mode */
  whatIfScenario?: WhatIfScenario | null;
  /** Called when the user commits or discards a what-if scenario */
  onWhatIfComplete?: (navigateBackToHeatmap?: boolean) => void;
  /** Called when a rebalance scenario is committed — carries before/after utilisation */
  onWhatIfCommitted?: (result: WhatIfCommitResult) => void;
  /** Heatmap-mode only: called when Apply/Assign is pressed in the suggestions panel */
  onNavigateToCalendarWithWhatIf?: (scenario: WhatIfScenario) => void;
  /** Heatmap-mode only: a commit result lifted from the calendar view — seeds the resolved banner */
  initialResolvedCommit?: WhatIfCommitResult | null;
  /** Heatmap-mode only: called once the initialResolvedCommit has been applied */
  onResolvedCommitConsumed?: () => void;
  /** Plan preview scenario from the Open Demand page — shows ghost bookings on the calendar */
  planPreviewScenario?: PlanPreviewScenario | null;
  /** Called when the user dismisses the plan preview banner */
  onPlanPreviewClose?: () => void;
  /** One-shot view + date range to apply when arriving from the Resource Allocation report */
  plannerNavContext?: PlannerNavContext | null;
  /** Called once plannerNavContext has been applied */
  onPlannerNavContextConsumed?: () => void;
  /** Navigates to the Integrations settings tab from the no-company empty state */
  onNavigateToIntegrations?: () => void;
}

export function Dashboard({
  heatmapMode = false,
  initialProjectFilter,
  whatIfScenario,
  onWhatIfComplete,
  onWhatIfCommitted,
  onNavigateToCalendarWithWhatIf,
  initialResolvedCommit,
  onResolvedCommitConsumed,
  planPreviewScenario,
  onPlanPreviewClose,
  plannerNavContext,
  onPlannerNavContextConsumed,
  onNavigateToIntegrations,
}: DashboardProps) {
  return (
    // h-full + overflow-hidden: fills the flex-1 main area and prevents
    // this container from generating its own scrollbar — Bryntum Scheduler
    // manages all scrolling internally.
    <div className="h-full overflow-hidden">
      <ErrorBoundary>
        <ResourceCalendar
          initialHeatmapMode={heatmapMode}
          initialProjectFilter={initialProjectFilter}
          whatIfScenario={whatIfScenario}
          onWhatIfComplete={onWhatIfComplete}
          onWhatIfCommitted={onWhatIfCommitted}
          onNavigateToCalendarWithWhatIf={onNavigateToCalendarWithWhatIf}
          initialResolvedCommit={initialResolvedCommit}
          onResolvedCommitConsumed={onResolvedCommitConsumed}
          planPreviewScenario={planPreviewScenario}
          onPlanPreviewClose={onPlanPreviewClose}
          plannerNavContext={plannerNavContext}
          onPlannerNavContextConsumed={onPlannerNavContextConsumed}
          onNavigateToIntegrations={onNavigateToIntegrations}
        />
      </ErrorBoundary>
    </div>
  );
}
